import { useState } from 'react';
import styled, { css } from 'styled-components';
import { CollapseIcon } from '../../../components/Icons/CollapseIcon';
import { ExpandIcon } from '../../../components/Icons/ExpandIcon';
import { DragIndicator } from '../../CreateVehicle/icons/DragIndicator';
import { useField, useForm } from 'react-final-form';
import { GradeField } from '../components/grade';
import { PhotosView } from '../components/photos';
import { CheckboxQuestions } from '../components/checkbox/CheckboxField';
import { NotesIcon } from '../icons/notes';
import { NotesField } from '../components/notes';
import { TiresGradeSelection } from '../components/tires/gradeSelection';
import { SoundRecordingProvider, useSoundRecordingContext } from '../SoundRecorder/context';
import { SoundRecorder } from '../SoundRecorder';
import { OdometerImages } from '../OdometerImages';
import { RecordingPlayer } from '../SoundRecorder/RecordingPlayer';
import { SoundPlayer } from '../components/soundPlayer';
import { useGetVehicleByIdQuery } from 'services/api/upload-vehicle';
import { LicensePlateDate } from '../components/tires/licensePlateDate';
import { useVehicleEditing } from 'context/isAnotherUserEditingContext';
import { NotesTopIcon } from 'components/VehicleCommon/Notes/NotesTopIcon';
import { useTiresGrade } from '../hooks/useTiresGrade';
import { useSaveNotes } from '../hooks/useSaveNotes';
import { NotesInputField } from '../components/notes/NotesInputField';
import GptIcon from 'components/Icons/gpt.svg';
import { useUpdateImage2 } from '../hooks/useUpdateImage2';
import ImageIcon from 'components/Icons/Image_icon_active_large.svg';
import { Dialog } from '@material-ui/core';

type Props = {
  field: string;
  title: string;
};

const errorStyles = css`
  outline: 1px solid #d01c2e;
`;

const Wrapper = styled.div<{ withBorder?: boolean; background: string; disabled?: boolean; hasError?: boolean }>`
  background: ${props => props.background};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 8px 9px 8px;
  width: 100%;
  outline: ${props => (props.withBorder ? '1px solid #3994de' : 'none')};
  pointer-events: ${props => (props.disabled ? '0.7' : 1)};
  ${props => props.hasError && errorStyles}
`;

const HeaderControls = styled.div<{ disabled: boolean }>`
  display: flex;
  align-items: center;
  margin-left: auto;
  pointer-events: ${props => (props.disabled ? 'none' : 'auto')};
`;

const IconWrapper = styled.div<{ withAutoMargin?: boolean }>`
  display: flex;
  position: relative;
  margin-left: ${props => (props.withAutoMargin ? 'auto' : 0)};
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0px;
`;

const Title = styled.div`
  font-size: 12px;
  font-weight: 600;
  line-height: 14.06px;
  color: #000;
  margin-left: 8px;
`;

const fillColor = 'rgba(153, 153, 153, 1)';

const AutoCheckButton = styled.div`
  background: #3994de;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  line-height: 14.06px;
  color: #fff;
  height: 32px;
  width: 100%;
`;

export const themeOptions = {
  white: {
    background: '#fff',
    gradeBackground: '#2222220A'
  },
  grey: {
    background: '#F5F5F5',
    gradeBackground: '#fff'
  }
};

export const FinalReviewTireImage = ({ field: sectionId, title }: Props) => {
  const { changeGradeBasedOnNumber, value: numericGrade, imageIndex, aiValue, hasError } = useTiresGrade(sectionId);
  const imageSectionId = `exterior.subsections.tires.images.${imageIndex}`;
  const { input: messageInput } = useField(`${imageSectionId}.message`);
  const { input: aiMessageInput } = useField(`${imageSectionId}.ai_message`);

  const [isExpanded, setIsExpanded] = useState(true);
  const toggleExpanded = () => setIsExpanded(curr => !curr);

  const { hasAnotherEditor } = useVehicleEditing();

  const { input: notesInput } = useField(`${sectionId}.notes.message`);
  const {
    input: { value: notesStatus }
  } = useField(`${sectionId}.notes.status`);

  const {
    input: { value: label }
  } = useField(`${sectionId}.label`);

  const showPlateDate = sectionId === 'exterior.subsections.tires.subsections.license_plate_registration';

  const withBorder = true;
  const withSoundRecorder = sectionId.includes('how_engine_runs_overall');

  return (
    <Wrapper withBorder={withBorder} background={'#fff'} hasError={hasError}>
      <HeaderRow>
        <IconWrapper onClick={toggleExpanded}>
          {isExpanded ? <CollapseIcon fillColor={fillColor} /> : <ExpandIcon fillColor={fillColor} />}
        </IconWrapper>
        <Title>{title}</Title>
        <HeaderControls disabled={hasAnotherEditor}>
          <IconWrapper>
            <TiresGradeSelection highlightError sectionId={sectionId} />
          </IconWrapper>
          {showPlateDate && <LicensePlateDate sectionId={sectionId} />}
          {withSoundRecorder && <SoundRecorder />}
          <IconWrapper>
            <NotesIcon isActive={true} />
            {notesInput.value && <NotesTopIcon iconType={notesStatus === 'ai_used' ? 'ai' : 'checkmark'} />}
          </IconWrapper>
          <IconWrapper>
            <DragIndicator />
          </IconWrapper>
        </HeaderControls>
      </HeaderRow>
      {isExpanded && <TireSection sectionId={sectionId} />}
    </Wrapper>
  );
};

const background = '#fff';
const gradeBackground = '#2222220A';

type TireSectionProps = {
  sectionId: string;
};

const TireSection = ({ sectionId }: TireSectionProps) => {
  const { imageIndex, hasError } = useTiresGrade(sectionId);
  const imageSectionId = `exterior.subsections.tires.images.${imageIndex}`;
  const { input: messageInput } = useField(`${imageSectionId}.notes.message`, { subscription: { value: true } });
  const { input: aiMessageInput } = useField(`${imageSectionId}.notes.ai_message`, { subscription: { value: true } });
  const {
    input: { value: imageUrl }
  } = useField(`${imageSectionId}.url_thumb`, { subscription: { value: true } });
  // const saveNotes = useSaveNotes(imageSectionId, vehicleId);
  const saveImage = useUpdateImage2();
  const form = useForm();

  const saveRegularNotes = (newValue: string) => {
    form.change(`${imageSectionId}.notes.message`, newValue);
    saveImage(`exterior.subsections.tires`, imageIndex, { notes: newValue });
  };

  const saveAiNotes = (newValue: string) => {
    form.change(`${imageSectionId}.notes.ai_message`, newValue);
    saveImage(`exterior.subsections.tires`, imageIndex, { ai_message: newValue });
  };

  return (
    <>
      <NotesInputField
        autoFocus={false}
        initialValue={messageInput.value}
        save={saveRegularNotes}
        hasError={hasError}
      />
      <AiOverview hasError={hasError} sectionId={sectionId} imageUrl={imageUrl} />
      <NotesInputField autoFocus={false} initialValue={aiMessageInput.value} save={saveAiNotes} hasError={hasError} />
      <GradeField sectionId={sectionId} background={gradeBackground} />
    </>
  );
};

const AiWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const AiMessage = styled.div`
  color: ${props => (props.hasError ? '#d01c2e' : '#000')};
  font-size: 12px;
  font-weight: 600;
`;

const ImageIconWrapper = styled.div`
  width: 32px;
  height: 32px;
  background: #3994de1a;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AiOverview = ({
  sectionId,
  hasError,
  imageUrl
}: {
  sectionId: string;
  hasError?: boolean;
  imageUrl?: string;
}) => {
  const [imageDialogOpened, setImageDialogOpened] = useState(false);
  const openImageDialog = () => setImageDialogOpened(true);
  const closeImageDialog = () => setImageDialogOpened(false);
  return (
    <AiWrapper>
      <div>
        <GptIcon />
      </div>
      <AiMessage hasError={hasError}>Ai generated Text</AiMessage>
      <div style={{ flex: 1 }} />
      <TiresGradeSelection sectionId={sectionId} readOnly isAiValue highlightError />
      {imageUrl && (
        <ImageIconWrapper onClick={openImageDialog}>
          <ImageIcon />
        </ImageIconWrapper>
      )}
      {imageUrl && <ImageDialog isOpened={imageDialogOpened} close={closeImageDialog} imageUrl={imageUrl} />}
    </AiWrapper>
  );
};

type ImageDialogProps = {
  isOpened: boolean;
  imageUrl: string;
  close: () => void;
};

const ImageWrapper = styled.div`
  border-radius: 8px;
  border: 4px solid #fff;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledImage = styled.img`
  width: 100%;
  aspect-ratio: 4 / 3;
`;

const ImageDialog: React.FC<ImageDialogProps> = ({ isOpened, imageUrl, close }) => {
  if (!isOpened) return null;
  return (
    <Dialog style={{ zIndex: 9999999, margin: -24 }} open={isOpened} onBackdropClick={close} onClose={close}>
      <ImageWrapper>
        <StyledImage src={imageUrl} alt="Tire Image" />
      </ImageWrapper>
    </Dialog>
  );
};
